import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Megaphone, Users2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { inr, fdate, monthLabel, currentMonth } from "../lib/format";
import { PageHeader, FormDialog, ConfirmDialog, DataTable, StatCard, inputCls } from "../components/common";
import { Button } from "../components/ui/button";


const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Card"];

const TEAM_OPTIONS = [
  { value: "Marketing", label: "Marketing" },
  { value: "Staff", label: "Staff" },
];

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [teamFilter, setTeamFilter] = useState("all"); // all | marketing | staff | other
  const [form, setForm] = useState({ open: false, initial: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => {
    api.get(`/expenses${month ? `?month=${month}` : ""}`).then((r) => setRows(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  const isMarketing = (r) => {
    const t = (r.team_name || r.team_id || r.paid_by || "").toLowerCase();
    const c = (r.category || "").toLowerCase();
    return t === "marketing" || c.includes("marketing") || c.includes("ads") || c.includes("campaign");
  };

  const isStaff = (r) => {
    const t = (r.team_name || r.team_id || r.paid_by || "").toLowerCase();
    const c = (r.category || "").toLowerCase();
    return t === "staff" || c === "salary" || c.includes("staff") || c.includes("welfare");
  };

  const marketing = rows.filter(isMarketing).reduce((s, r) => s + (r.amount || 0), 0);
  const staff = rows.filter((r) => isStaff(r) && !isMarketing(r)).reduce((s, r) => s + (r.amount || 0), 0);
  const projectExp = rows.filter((r) => r.project_id && !isMarketing(r) && !isStaff(r)).reduce((s, r) => s + (r.amount || 0), 0);
  const other = rows.filter((r) => !isMarketing(r) && !isStaff(r) && !r.project_id).reduce((s, r) => s + (r.amount || 0), 0);

  const fields = [
    { name: "date", label: "Date", type: "date", required: true },
    {
      name: "category",
      label: "Category",
      type: "text",
      required: true,
      placeholder: "Type expense category manually",
      autoComplete: "off",
    },
    { name: "amount", label: "Amount (₹)", type: "number", required: true },
    { name: "mode", label: "Payment Mode", type: "select", options: MODES.map((m) => ({ value: m, label: m })) },
    { name: "project_id", label: "Project (optional)", type: "select", options: projects.map((p) => ({ value: p.id, label: p.name })) },
    {
      name: "team_id",
      label: "Team (Marketing or Staff)",
      type: "select",
      placeholder: "Select Team...",
      options: TEAM_OPTIONS,
    },
    { name: "paid_by", label: "Paid By", placeholder: "e.g. Marketing, Staff, Admin" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];

  const submit = async (v) => {
    const teamVal = v.team_id || "";
    const payload = {
      date: v.date,
      category: (v.category || "").trim(),
      amount: parseFloat(v.amount) || 0,
      mode: v.mode || "Cash",
      project_id: v.project_id || "",
      team_id: teamVal,
      team_name: teamVal,
      paid_by: v.paid_by || (teamVal ? teamVal : "Admin"),
      description: v.description || "",
    };
    if (form.initial?.id) {
      await api.put(`/expenses/${form.initial.id}`, payload);
      toast.success("Expense updated");
    } else {
      await api.post("/expenses", payload);
      toast.success("Expense added");
    }
    load();
  };

  const filteredRows = rows.filter((r) => {
    if (teamFilter === "all") return true;
    if (teamFilter === "marketing") return isMarketing(r);
    if (teamFilter === "staff") return isStaff(r);
    if (teamFilter === "other") return !isMarketing(r) && !isStaff(r);
    return true;
  });

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Company Expenses" sub={month ? `Expenses for ${monthLabel(month)}` : "All expenses"} testid="expenses-header">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls + " w-auto"} data-testid="expenses-month-filter" />
        <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setForm({ open: true, initial: { date: new Date().toISOString().slice(0, 10) } })} data-testid="add-expense-button">
          <Plus className="mr-1 h-4 w-4" /> Add Expense
        </Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Expenses" value={inr(total)} tone="red" testid="expenses-total" />
        <StatCard label="Marketing" value={inr(marketing)} testid="expenses-marketing" sub="Marketing team & ads" />
        <StatCard label="Staff Expenses" value={inr(staff)} testid="expenses-project" sub="Staff & operations" />
        <StatCard label="Other / Projects" value={inr(other + projectExp)} testid="expenses-other" sub="Office rent, fuel, etc." />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={teamFilter === "all" ? "default" : "outline"}
            onClick={() => setTeamFilter("all")}
            className="rounded-full text-xs h-8 px-3"
          >
            All Expenses ({rows.length})
          </Button>
          <Button
            size="sm"
            variant={teamFilter === "marketing" ? "default" : "outline"}
            onClick={() => setTeamFilter("marketing")}
            className="rounded-full text-xs h-8 px-3 border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            📢 Marketing ({rows.filter(isMarketing).length})
          </Button>
          <Button
            size="sm"
            variant={teamFilter === "staff" ? "default" : "outline"}
            onClick={() => setTeamFilter("staff")}
            className="rounded-full text-xs h-8 px-3 border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            👥 Staff ({rows.filter(isStaff).length})
          </Button>
          <Button
            size="sm"
            variant={teamFilter === "other" ? "default" : "outline"}
            onClick={() => setTeamFilter("other")}
            className="rounded-full text-xs h-8 px-3 text-slate-600 hover:bg-slate-100"
          >
            Other ({rows.filter((r) => !isMarketing(r) && !isStaff(r)).length})
          </Button>
        </div>
        <p className="text-xs text-slate-500 font-medium">
          Showing {filteredRows.length} of {rows.length} expenses
        </p>
      </div>

      <DataTable
        testid="expenses-table"
        columns={[
          { key: "date", label: "Date", render: (r) => fdate(r.date) },
          { key: "category", label: "Category", render: (r) => (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-800 border border-slate-200">
              {r.category}
            </span>
          )},
          { key: "description", label: "Description", render: (r) => r.description || "-" },
          { key: "project_name", label: "Project", render: (r) => r.project_name || "-" },
          { key: "team_name", label: "Team", render: (r) => {
            const team = r.team_name || r.team_id || (r.paid_by?.toLowerCase() === "marketing" ? "Marketing" : r.paid_by?.toLowerCase() === "staff" ? "Staff" : "");
            if (!team || team === "-") return <span className="text-slate-400 text-xs">-</span>;
            const isMkt = team.toLowerCase() === "marketing";
            const isStf = team.toLowerCase() === "staff";
            return (
              <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold border ${
                isMkt ? "bg-purple-100 text-purple-800 border-purple-200" : isStf ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"
              }`}>
                {isMkt ? "📢 Marketing" : isStf ? "👥 Staff" : team}
              </span>
            );
          }},
          { key: "amount", label: "Amount", align: "right", render: (r) => <span className="font-semibold text-red-700">{inr(r.amount)}</span> },
          { key: "mode", label: "Mode" },
          { key: "paid_by", label: "Paid By" },
          { key: "actions", label: "", render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" aria-label="Edit expense" data-testid={`edit-expense-${r.id}`} onClick={() => setForm({ open: true, initial: r })}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" aria-label="Delete expense" className="text-red-600" data-testid={`delete-expense-${r.id}`} onClick={() => setDel(r)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )},
        ]}
        rows={filteredRows}
      />

      <FormDialog
        open={form.open}
        onOpenChange={(o) => setForm({ open: o, initial: null })}
        title={form.initial?.id ? "Edit Expense" : "Add Expense"}
        fields={fields}
        initial={form.initial}
        onSubmit={submit}
        testid="expense-form"
      />
      <ConfirmDialog
        open={!!del}
        onOpenChange={() => setDel(null)}
        title="Delete Expense"
        message={`Delete expense of ${inr(del?.amount)} (${del?.category})?`}
        onConfirm={async () => {
          await api.delete(`/expenses/${del.id}`);
          toast.success("Expense deleted");
          load();
        }}
        testid="expense-delete-confirm"
      />
    </div>
  );
}
